// ============================================================================
// Doc Order — completeness + workflow gates (spec A1.1–A1.5).
// The edge function re-validates every gate server-side at transition time;
// the UI reads the same functions for live feedback. Never fork these rules.
// ============================================================================

import { feeNeedsDisposition, feeReconciliationWorkflowOpen } from './fees.ts';
import type {
  ChecklistDoc,
  DocOrderFee,
  DocOrderLoanFacts,
  DocOrderRole,
  DocOrderStage,
  InsuranceInfo,
  ManualClosingFields,
} from './types.ts';

/** A1.1 — edit rights. All other role/stage combinations are read-only. */
export function isEditable(role: DocOrderRole, stage: DocOrderStage): boolean {
  return (role === 'processor' && stage === 'draft') || (role === 'closing' && stage === 'submitted');
}

/** A1.2 — count of the six required manual closing fields. */
export function reqFilled(m: ManualClosingFields): number {
  return [m.signingDate, m.closingDate, m.firstPaymentDate, m.powerOfAttorney, m.signType, m.docEmail].filter(
    Boolean
  ).length;
}

export interface Section4Gate {
  ok: boolean;
  primaryFilled: boolean;
  needsDic: boolean;
  dicFilled: boolean;
  hoaCovers: boolean;
}

/** A1.3 — §4 Insurance completeness. */
export function section4Gate(loan: Pick<DocOrderLoanFacts, 'purpose' | 'propertyType'>, s: InsuranceInfo): Section4Gate {
  const isPurchase = /purchase/i.test(loan.purpose || '');
  const isCondo = /condo/i.test(loan.propertyType || '');
  const hoaCovers = isCondo && !!s.hoaMasterCoversWallsIn;
  const needsDic = /fair\s*plan/i.test(s.company || '') && !s.dicExtendedCoverages;
  const primaryFilled = !!(s.company && s.premium && s.phone && (!isPurchase || s.effective));
  const dicFilled = !!(s.dicCompany && s.dicPremium && s.dicPhone && (!isPurchase || s.dicEffective));
  const ok = s.deferred || hoaCovers || (primaryFilled && (!needsDic || dicFilled));
  return { ok, primaryFilled, needsDic, dicFilled, hoaCovers };
}

export interface Section5Gate {
  ok: boolean;
  feesNeedTiming: number;
  creditChoicePending: boolean;
  reconciliationsOpen: number;
}

/** A1.3 — §5 fee completeness: disposition plus reconciliation workflow. */
export function section5Gate(
  fees: DocOrderFee[],
  invoiceFolderStatus: Readonly<Record<string, boolean>>
): Section5Gate {
  const feesNeedTiming = fees.filter((f) => feeNeedsDisposition(f)).length;
  const creditChoicePending = fees.some(
    (f) =>
      f.refreshCredit !== undefined &&
      f.refreshCredit === null &&
      Number(f.amount) > 0 &&
      !f.notApplicable
  );
  const reconciliationsOpen = fees.filter((f) =>
    feeReconciliationWorkflowOpen(f, invoiceFolderStatus[f.feeKey] ?? false)
  ).length;
  return {
    ok: feesNeedTiming === 0 && !creditChoicePending && reconciliationsOpen === 0,
    feesNeedTiming,
    creditChoicePending,
    reconciliationsOpen,
  };
}

/** A1.3 — §6: every required, gated-in DERIVED doc is received. */
export function section6Gate(derivedDocs: ChecklistDoc[], gatesSource: Record<string, unknown>): {
  ok: boolean;
  missing: ChecklistDoc[];
} {
  const applicable = derivedDocs.filter((d) => d.required && (!d.gated || !!gatesSource[d.gated]));
  const missing = applicable.filter((d) => d.status !== 'received');
  return { ok: missing.length === 0, missing };
}

export interface SubmitGate {
  canSubmit: boolean;
  reqFilled: number;
  vestingConfirmed: boolean;
  section5Ok: boolean;
  signed: boolean;
}

/** A1.2 — canSubmit = reqFilled===6 && vestingConfirmed && section5Ok && signed. */
export function submitGate(args: {
  manual: ManualClosingFields;
  vestingConfirmed: boolean;
  fees: DocOrderFee[];
  invoiceFolderStatus: Readonly<Record<string, boolean>>;
  signed: boolean;
}): SubmitGate {
  const filled = reqFilled(args.manual);
  const s5 = section5Gate(args.fees, args.invoiceFolderStatus);
  return {
    canSubmit: filled === 6 && args.vestingConfirmed && s5.ok && args.signed,
    reqFilled: filled,
    vestingConfirmed: args.vestingConfirmed,
    section5Ok: s5.ok,
    signed: args.signed,
  };
}

/**
 * A1.4 — insurance defer gate (last resort): everything else must be complete;
 * the ONLY outstanding items allowed are the EOI doc + §4 fields. A reason is
 * required to defer-and-sign (enforced by the caller).
 */
export function canDeferInsurance(args: {
  manual: ManualClosingFields;
  vestingConfirmed: boolean;
  fees: DocOrderFee[];
  invoiceFolderStatus: Readonly<Record<string, boolean>>;
  otherReqDocsMissing: number;
}): boolean {
  const s5 = section5Gate(args.fees, args.invoiceFolderStatus);
  return (
    reqFilled(args.manual) === 6 &&
    args.vestingConfirmed &&
    s5.ok &&
    args.otherReqDocsMissing === 0
  );
}

export interface SendToLoGate {
  allApproved: boolean;
  unapproved: number;
  vestingNeedsConfirm: boolean;
}

/**
 * A1.5 — Closing → LO send gate. Vesting attestation is required ONLY when the
 * Encompass value differs from the submitted vesting.
 */
export function sendToLoGate(args: {
  fees: DocOrderFee[];
  gatesSource: Record<string, unknown>;
  vesting: string | null;
  encompassVesting: string | null;
  encompassVestingConfirmed: boolean;
}): SendToLoGate {
  const closingFees = args.fees.filter(
    (f) => Number(f.amount) > 0 && !f.notApplicable && (!isGated(f) || !!args.gatesSource[gateKey(f)])
  );
  const unapproved = closingFees.filter((f) => !f.approvedBy).length;
  // An empty Field 1867 is nothing to attest. Doc Order treats Encompass as
  // the vesting source of truth and never writes 1867; confirmation is only
  // meaningful when a real Encompass value exists and differs from the
  // processor submission (the checkbox renders under that same condition).
  const encVesting = (args.encompassVesting ?? '').trim();
  const vestingDiffers = encVesting !== '' && encVesting !== (args.vesting ?? '');
  const vestingNeedsConfirm = vestingDiffers && !args.encompassVestingConfirmed;
  return { allApproved: unapproved === 0 && !vestingNeedsConfirm, unapproved, vestingNeedsConfirm };
}

// Vestigial fee gating (audit C3): no current fee sets a gate key — kept as a
// no-op shim so the closing filter stays shape-identical to the prototype.
function isGated(_f: DocOrderFee): boolean {
  return false;
}
function gateKey(_f: DocOrderFee): string {
  return '';
}

/** Stepper projection: 5 visual steps over 4 persisted stages (ops #4). */
export const DO_VISUAL_STAGES = [
  { key: 'draft', label: 'Draft', owner: 'Processor' },
  { key: 'submitted', label: 'Submitted', owner: 'Closing' },
  { key: 'confirmed', label: 'Doc Order Completed', owner: 'Closing' },
  { key: 'lo_review', label: 'LO Review', owner: 'LO' },
  { key: 'complete', label: 'Complete', owner: '—' },
] as const;

/** Index into DO_VISUAL_STAGES for a persisted stage ('confirmed' is passed-through visually). */
export function visualStageIndex(stage: DocOrderStage): number {
  switch (stage) {
    case 'draft':
      return 0;
    case 'submitted':
      return 1;
    // once the order reaches lo_review, the 'confirmed' milestone reads as passed
    case 'lo_review':
      return 3;
    case 'complete':
      return 4;
  }
}
